import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Modal } from '../elements/Modal';
import { Icon } from '../elements/icon';
import Header from '../../Layout/Header';
import useOmega3Addon from './hooks/useOmega3Addon';
import { useLazyGetOrderIdQuery } from '../../features/metaApiSlice';
import useOmega3Purchase from './hooks/useOmega3Purchase';
import Omega3Hero from './components/Omega3Hero';
import Omega3PlanSelector from './components/Omega3PlanSelector';
import Omega3Reviews from './components/Omega3Reviews';
import Omega3Faq from './components/Omega3Faq';
import Omega3StickyCta from './components/Omega3StickyCta';
import Omega3DifferenceSection from './components/Omega3DifferenceSection';
import Omega3BenefitsSection from './components/Omega3BenefitsSection';
import Omega3HowToSection from './components/Omega3HowToSection';
import Omega3IngredientsSection from './components/Omega3IngredientsSection';
import Omega3PromoBar from './components/Omega3PromoBar';
import { getBrowserLocale } from '../../utils/language';

const Omega3AddonPage = () => {
  const promoDeadline = '2026-04-20 23:59:00';
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const quizId = searchParams.get('quizId');
  const [quizSubscription, setQuizSubscription] = useState(null);
  const [isQuizSubscriptionLoading, setIsQuizSubscriptionLoading] = useState(false);
  const [hasResolvedQuizSubscription, setHasResolvedQuizSubscription] = useState(false);
  const [getOrderId] = useLazyGetOrderIdQuery();
  const {
    isLoading,
    plans,
    selectedPlan,
    selectedPlanKey,
    setSelectedPlanKey,
    addToSubscription,
    hasMatchingPlan,
    hasActiveStripeSubscription,
    hasAnyActiveSubscription,
  } = useOmega3Addon({ subscriptionOverride: quizSubscription });
  const {
    isLoading: isPurchaseLoading,
    plans: purchasePlans,
    selectedPlan: purchaseSelectedPlan,
    selectedPlanKey: purchasePlanKey,
    setSelectedPlanKey: setPurchasePlanKey,
    startCheckout,
  } = useOmega3Purchase({ source: 'omega3_addon_page' });
  const [showStickyCta, setShowStickyCta] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [separatePurchaseOpen, setSeparatePurchaseOpen] = useState(false);

  const isPageLoading = isLoading || isQuizSubscriptionLoading;

  const loginRedirectPath = useMemo(() => {
    const query = quizId ? `?quizId=${encodeURIComponent(quizId)}` : '';
    return `/products/omega-3/addon${query}`;
  }, [quizId]);

  const priceFormatter = new Intl.NumberFormat(getBrowserLocale(i18n.language), {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

  useEffect(() => {
    let isMounted = true;

    if (!quizId) {
      setQuizSubscription(null);
      setIsQuizSubscriptionLoading(false);
      setHasResolvedQuizSubscription(true);
      return undefined;
    }

    setIsQuizSubscriptionLoading(true);
    setHasResolvedQuizSubscription(false);
    getOrderId(quizId)
      .unwrap()
      .then((response) => {
        if (!isMounted) return;
        setQuizSubscription(response?.data?.purchasedSubscription || null);
      })
      .catch(() => {
        if (!isMounted) return;
        setQuizSubscription(null);
      })
      .finally(() => {
        if (!isMounted) return;
        setIsQuizSubscriptionLoading(false);
        setHasResolvedQuizSubscription(true);
      });

    return () => {
      isMounted = false;
    };
  }, [getOrderId, quizId]);

  useEffect(() => {
    if (!hasResolvedQuizSubscription) return;
    if (quizSubscription?.product_type === 'riise_formula') return;
    if (localStorage.getItem('customer_token')) return;

    navigate(`/login?redirect=${encodeURIComponent(loginRedirectPath)}`);
  }, [hasResolvedQuizSubscription, loginRedirectPath, navigate, quizSubscription]);

  useEffect(() => {
    const handleScroll = () => {
      setShowStickyCta(window.scrollY > 700);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleAddToSubscription = async () => {
    try {
      const result = await addToSubscription();

      if (result?.alreadyExists) {
        toast.error(result?.message || t('Omega3_Addon_Error'));
        return;
      }

      if (result?.success) {
        toast.success(result?.message || t('Omega3_Addon_Success'));
        setShowConfirmModal(false);
        window.setTimeout(() => {
          navigate('/');
        }, 2000);
      }
    } catch (error) {
      toast.error(error?.data?.message || error?.message || t('Omega3_Addon_Error'));
    }
  };

  const showUnavailableState = !isPageLoading && (!hasActiveStripeSubscription || !hasMatchingPlan);
  const unavailableTitleKey = hasAnyActiveSubscription
    ? 'Omega3_Addon_NoEligibleSub_Title'
    : 'Omega3_Addon_NoSub_Title';
  const unavailableBodyKey = hasAnyActiveSubscription
    ? 'Omega3_Addon_NoEligibleSub_Body'
    : 'Omega3_Addon_NoSub_Body';
  const stickyUsesSeparatePurchase = showUnavailableState || separatePurchaseOpen;
  const stickyPlan = stickyUsesSeparatePurchase ? purchaseSelectedPlan : selectedPlan;
  const stickyHidden =
    !showStickyCta ||
    (stickyUsesSeparatePurchase
      ? isPurchaseLoading || !purchaseSelectedPlan?.paymentLink
      : isPageLoading || !selectedPlan || showUnavailableState);

  return (
    <>
      <Header />
      <Omega3Hero>
        {isPageLoading ? (
          <div className="t-rounded-2xl t-border t-border-g200 t-bg-white t-p-6 t-text-center">
            <p className="t-m-0 t-text-g500">{t('Omega3_Addon_Loading')}</p>
          </div>
        ) : showUnavailableState ? (
          <div className="t-flex t-flex-col t-gap-6">
            <div className="t-rounded-2xl t-border t-border-g200 t-bg-white t-p-6 t-text-center">
              <h3
                className="t-mb-3 t-text-[24px] t-font-normal t-text-navy"
                style={{ fontFamily: 'DM Serif Display, serif' }}
              >
                {t(unavailableTitleKey)}
              </h3>
              <p className="t-m-0 t-text-g500">{t(unavailableBodyKey)}</p>
            </div>
            <Omega3PlanSelector
              plans={purchasePlans}
              selectedPlanKey={purchasePlanKey}
              onSelect={setPurchasePlanKey}
              onCheckout={startCheckout}
              checkoutDisabled={isPurchaseLoading || !purchaseSelectedPlan?.paymentLink}
            />
          </div>
        ) : (
          <Omega3PlanSelector
            plans={plans}
            selectedPlanKey={selectedPlanKey}
            onSelect={setSelectedPlanKey}
            onCheckout={() => setShowConfirmModal(true)}
            checkoutDisabled={isPageLoading || !selectedPlan}
            hideToggle
            ctaLabelKey="Omega3_Addon_Cta"
            titleKey="Omega3_Addon_Title"
            belowPrimaryCta={
              <div className="t-mt-3 t-rounded-2xl t-border t-border-g200 t-bg-white t-overflow-hidden">
                <button
                  type="button"
                  aria-expanded={separatePurchaseOpen}
                  onClick={() => setSeparatePurchaseOpen((open) => !open)}
                  className="t-flex t-w-full t-items-center t-justify-between t-gap-3 t-border-0 t-bg-surface t-px-4 t-py-3.5 t-text-left t-cursor-pointer hover:t-bg-butter/20 t-transition-colors"
                >
                  <span className="t-text-[15px] t-font-semibold t-text-navy">
                    {t('Omega3_Addon_SeparatePurchase_Toggle')}
                  </span>
                  <Icon
                    name="chevronDown"
                    raw
                    size="20"
                    className={`t-text-quiz-teal t-flex-shrink-0 t-transition-transform ${
                      separatePurchaseOpen ? 't-rotate-180' : ''
                    }`}
                  />
                </button>
                {separatePurchaseOpen && (
                  <div className="t-border-t t-border-g200 t-bg-white t-p-4 md:t-p-5">
                    <Omega3PlanSelector
                      plans={purchasePlans}
                      selectedPlanKey={purchasePlanKey}
                      onSelect={setPurchasePlanKey}
                      onCheckout={startCheckout}
                      checkoutDisabled={isPurchaseLoading || !purchaseSelectedPlan?.paymentLink}
                    />
                  </div>
                )}
              </div>
            }
          />
        )}
      </Omega3Hero>
      <Omega3DifferenceSection />
      <Omega3BenefitsSection />
      <Omega3HowToSection />
      <Omega3IngredientsSection />
      <Omega3Reviews />
      <Omega3Faq />
      <Omega3StickyCta
        selectedPlan={stickyPlan}
        onCheckout={() => (stickyUsesSeparatePurchase ? startCheckout() : setShowConfirmModal(true))}
        hidden={stickyHidden}
      />
      <Modal
        open={showConfirmModal}
        handleClose={() => setShowConfirmModal(false)}
        className="!t-max-w-[95vw] lg:t-max-w-[420px]"
      >
        <div className="t-pb-4 t-flex t-flex-col t-gap-4 t-w-full">
          <h3 className="t-m-0 t-text-xl t-font-semibold t-text-center t-text-navy">
            {t('Omega3_Addon_Confirm_Title')}
          </h3>
          <p className="t-m-0 t-text-sm t-text-g500 t-text-center">{t('Omega3_Addon_Confirm_Body')}</p>
          <div className="t-rounded-lg t-bg-surface t-p-3 t-text-sm t-flex t-items-center t-justify-between t-gap-3">
            <span className="t-text-g500">{t('Omega3_Addon_Confirm_Amount')}</span>
            <strong className="t-text-navy">
              {selectedPlan ? priceFormatter.format(selectedPlan.amount) : '-'}
            </strong>
          </div>
          <div className="t-flex t-items-center t-gap-3">
            <button
              type="button"
              className="t-flex-1 t-inline-flex t-items-center t-justify-center t-rounded-lg t-px-4 t-py-2.5 t-text-sm t-font-semibold t-bg-butter t-text-navy hover:t-brightness-95 disabled:t-opacity-60"
              onClick={handleAddToSubscription}
              disabled={isPageLoading || !selectedPlan}
            >
              {t('Omega3_Addon_Confirm_Button')}
            </button>
            <button
              type="button"
              className="t-flex-1 t-inline-flex t-items-center t-justify-center t-rounded-lg t-px-4 t-py-2.5 t-text-sm t-font-semibold t-bg-g200 t-text-navy hover:t-bg-g300"
              onClick={() => setShowConfirmModal(false)}
            >
              {t('Omega3_Addon_Confirm_Cancel')}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default Omega3AddonPage;
